import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, Trash2, Truck, ChevronDown, ChevronUp } from 'lucide-react';
import BackBar from '../components/BackBar';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleAccessBuilder = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showGuide, setShowGuide] = useState(true);

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      const { data } = await axios.get(`${API}/vehicle-access`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createProject = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/vehicle-access`, { name: newName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/vehicle-access/${data.id}`);
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this vehicle access project?')) return;
    try {
      await axios.delete(`${API}/vehicle-access/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <BackBar to="/dashboard" label="Dashboard" />
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-slate-600" data-testid="back-btn"><ArrowLeft size={20} /></button>
            <div className="p-2 bg-orange-100 rounded-lg"><Truck size={20} className="text-orange-600" /></div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Vehicle Access Planner</h1>
              <p className="text-xs text-slate-400">Preliminary swept path assessment tool</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-4 text-sm text-amber-800" data-testid="disclaimer-banner">
          <strong>Preliminary design tool only.</strong> This assists with early compliance checking for vehicle access. Professional traffic engineering assessment is required for final DA submission.
        </div>

        {/* How to Use */}
        <div className="bg-white rounded-xl border border-slate-200 mb-6" data-testid="how-to-use-card">
          <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-5 py-3 text-left" data-testid="how-to-use-toggle">
            <span className="text-sm font-semibold text-slate-700">How to Use</span>
            {showGuide ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showGuide && (
            <div className="px-5 pb-4 border-t border-slate-100 pt-3">
              <ol className="space-y-1.5 text-sm text-slate-600">
                <li><span className="font-semibold text-slate-800">1.</span> Create a project and upload your site plan (PNG/JPG)</li>
                <li><span className="font-semibold text-slate-800">2.</span> Set the scale — pick a preset <strong>or</strong> use <strong>Calibrate</strong> (click two points with a known distance for accuracy)</li>
                <li><span className="font-semibold text-slate-800">3.</span> Select a vehicle (SRV 6.4m, MRV 8.8m, or HRV 12.5m)</li>
                <li><span className="font-semibold text-slate-800">4.</span> Click <strong>Add Path</strong>, then click the canvas to drop waypoints along the truck route</li>
                <li><span className="font-semibold text-slate-800">5.</span> Click <strong>Analyse</strong> to check turning radii and reversing compliance</li>
                <li><span className="font-semibold text-slate-800">6.</span> Export to <strong>PDF</strong> for your DA submission pack</li>
              </ol>
              <p className="text-xs text-slate-400 mt-3"><strong>Calibrate vs Scale:</strong> Scale presets (1:100, 1:200, etc.) are approximate. For best accuracy, use Calibrate — click two points on your plan where you know the real-world distance, then enter that distance in mm.</p>
            </div>
          )}
        </div>

        {/* Create new */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center gap-3">
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createProject()}
              placeholder="New project name (e.g. 42 Smith St — Vehicle Access)" className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              data-testid="new-project-input" />
            <button onClick={createProject} disabled={creating || !newName.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white text-sm font-medium rounded-xl hover:bg-orange-700 disabled:opacity-40 transition-colors"
              data-testid="create-project-btn">
              <Plus size={16} /> Create
            </button>
          </div>
        </div>

        {/* Project list */}
        {loading ? (
          <div className="text-center text-slate-400 py-12">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Truck size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No vehicle access projects yet. Create one above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(p => (
              <div key={p.id} onClick={() => navigate(`/vehicle-access/${p.id}`)}
                className="bg-white rounded-xl border border-slate-200 p-5 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all group"
                data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-orange-50 rounded-lg"><Truck size={18} className="text-orange-500" /></div>
                  <button onClick={e => deleteProject(p.id, e)} className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" data-testid={`delete-project-${p.id}`}><Trash2 size={14} /></button>
                </div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1 truncate">{p.name}</h3>
                <p className="text-[11px] text-slate-400">{new Date(p.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleAccessBuilder;
